'use client'

import { useTranslations } from "next-intl"
import styles from "./page.module.css"
import { IconButton, InputAdornment, TextField } from "@mui/material"
import { FormEvent, useEffect, useMemo, useRef, useState } from "react"
import { getFieldError, toBase64, validateForm } from "@/utils/helpers"
import Button, { ButtonSize } from "@/components/UI/Buttons/Button/Button"
import AppLink from "@/components/UI/Links/AppLink/AppLink"
import { isEmail, isRequired } from "@/utils/validators"
import IconShowPassword from '@/assets/img/Icon_Show-Password.svg'
import IconHidePassword from '@/assets/img/Icon_Hide-Password.svg'
import { FieldState, ValidationsForForm } from "@/interfaces/api/forms/forms_types"
import { LoginErrorsSubtypes, LoginErrorsTypes } from "@/interfaces/api/login/api_request_types"
import { API_Error } from "@/interfaces/api/response/api_response_types"
import TooltipField from "@/components/UI/Tooltips/TooltipField/TooltipField"
import Notification from "@/components/Notification/Notification"
import { toast } from "react-toastify"
import { useQueryClient } from "@tanstack/react-query"
import { API_Data_Self_Response } from "@/interfaces/api/self/api_response_types"
import { API_Data_Profiles_Response } from "@/interfaces/api/profiles/api_response_types"
import { getSelf } from "@/apiUtilities/requests/user"
import { listProfiles } from "@/apiUtilities/requests/profiles"
import { useUserStore } from "@/stores/useUserStore"
import { useLogout } from "@/apiUtilities/mutations/useLogout"
import { useLogin } from "@/apiUtilities/mutations/useLogin"
import { useRouter } from "@/i18n/navigation"
import { useAuthStore } from "@/stores/useAuthStore"
import { FEATURES } from "@/utils/constants"
import { useDynamicSignUpURL } from "@/apiUtilities/queries/useDynamicSignUpURL"
import { useDynamicRecoverPasswordURL } from "@/apiUtilities/queries/useDynamicRecoverPassURL"
import { useModalStore } from "@/stores/useModalStore"
import SpeedTest from "@/components/SpeedTest/SpeedTest"
import IconSpeedTest from "@/assets/img/Icon_Speed-Test.svg"
interface LoginFormState {
  username: FieldState<string>
  password: FieldState<string>
}

/**
 * Initial (empty) validations object for the login form.
 */
const initialValidations: ValidationsForForm<LoginFormState> = {
  username: [],
  password: []
}

export default function Login() {
  const t = useTranslations('Login')
  const te = useTranslations('Errors')
  const login = useLogin()
  const serverLogout = useLogout()
  const router = useRouter()
  const { setUser } = useUserStore()
  const { setCredentials } = useAuthStore()
  const openModal = useModalStore((state) => state.openModal)
  const queryClient = useQueryClient()
  const { data: dynamicSignUpURLData } = useDynamicSignUpURL({
    enabled: FEATURES.signUpExternal && FEATURES.signUpExternalDynamic
  })

  console.log('ENABLED: ', FEATURES.recoverPasswordExternal && FEATURES.recoverPasswordExternalDynamic)
  const { data: dynamicRecoverPasswordURLData } = useDynamicRecoverPasswordURL({
    enabled: FEATURES.recoverPasswordExternal && FEATURES.recoverPasswordExternalDynamic
  })
  const userNameRef = useRef<HTMLInputElement | null>(null)
  const [formIsValid, setFormIsValid] = useState<boolean>(false)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [formState, setFormState] = useState<LoginFormState>({
    username: {
      value: '',
      dirty: false,
      required: true,
      validators: [
        { validator: isRequired, show: false },
        { validator: isEmail, show: false }
      ]
    },
    password: {
      value: '',
      dirty: false,
      required: true,
      validators: [
        { validator: isRequired, show: false }
      ]
    }
  })
  const [validations, setValidations] = useState<ValidationsForForm<LoginFormState>>(initialValidations)
  const [showServerError, setShowServerError] = useState<string>('')
  const [usernameFocused, setUsernameFocused] = useState<boolean>(false)
  const signUpLink = useMemo(() => {
    if (!FEATURES.signUp) return '/login'
    if (!FEATURES.signUpExternal) return '/#plans'
    if (!FEATURES.signUpExternalDynamic) {
      return process.env.NEXT_PUBLIC_FEATURE_SIGNUP_EXTERNAL_LINK ?? ''
    }
    return dynamicSignUpURLData?.url || ''
  }, [dynamicSignUpURLData])

  const recoverPasswordLink = useMemo(() => {
    if (!FEATURES.recoverPasswordExternal) return '/login/forgot-password'
    if (!FEATURES.signUpExternalDynamic) {
      (process.env.NEXT_PUBLIC_FEATURE_RECOVER_PASSWORD_EXTERNAL_LINK ?? '')
    }
    return dynamicRecoverPasswordURLData?.url || ''
  }, [dynamicRecoverPasswordURLData])

  /**
   * Handles input changes for both username and password fields.
   * Marks the field as `dirty` and updates its `value`.
   * @param e Input change event from the TextField.
   */
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const key = e.target.name as keyof LoginFormState
    if (!!showServerError) setShowServerError('')
    setFormState(formStatePrev => {
      return {
        ...formStatePrev,
        [key]: {
          ...formStatePrev[key],
          value: e.target.value,
          dirty: e.target.value !== ''
        }
      }
    })
  }

  /**
   * Handles login API errors and updates UI state accordingly.
   *
   * @param response - The API error response returned by the backend.
   */
  async function handleErrorLogin (response: API_Error) {

    if(response.code === 401 && response.description === LoginErrorsTypes.UNAUTHORIZED) {
      setShowServerError('unauthorized')
      return
    }

    if(response.code === 401 && (!!response.errors && response.errors[0].errno === LoginErrorsSubtypes.EMAIL_NOT_CONFIRMED)) {
      toast.error(<Notification text={t('errors.server.unverifiedEmail')} />)
      setCredentials(formState.username.value, formState.password.value, response.data.user_id)
      router.push('/verify-email')
      return
    }

    if(response.code === 401 && response.data && (response.data.missing_actions || response.data.missing_mandatory_fields)) {
          setCredentials(formState.username.value, formState.password.value, response.data.user_id)
          return
    }
    // This means account incomplete
    if(!response.data) toast.error(<Notification text={te('defaultUnknowError')} />)
  }

  /**
   * After login succeeds, decide whether we need the profile selector:
   * - Fetch `self` and `profiles` (cached in React Query)
   * - Otherwise, go to /select-profile
   * - If anything fails, logout and show a generic error
   */
  async function handleSuccessLogin () {
    try {
      const [self, _profiles] = await Promise.all([
        queryClient.fetchQuery<API_Data_Self_Response>({
          queryKey: ['self'],
          queryFn: getSelf,
        }),
        queryClient.fetchQuery<API_Data_Profiles_Response>({
          queryKey: ['profiles'],
          queryFn: listProfiles,
        }),
      ])

      setUser(self.id)
      router.push('/select-profile')
    } catch (err) {
      const apiErr = err as API_Error
      console.error(apiErr?.description ?? err)
      try {
        await serverLogout.mutateAsync()
      } finally {
        toast.error(<Notification text={te('defaultUnknowError')} />)
        router.replace('/home')
      }
    }
  }

  /**
   * Handles form submission:
   * - prevents the default browser submit
   * - creates the Basic Auth header value (base64 "username:password")
   * - triggers the login mutation
   * @param e Form submit event.
   */
  function handleSubmit(e:FormEvent) {
    e.preventDefault()

    const basicAuth = toBase64(formState.username.value + ":" + formState.password.value)
    login.mutate(basicAuth,
      {
        onSuccess: () => handleSuccessLogin(),
        onError: (response) => handleErrorLogin(response)
      }
    )
  }

  /**
   * Re-validates the form whenever a field value changes.
   * Updates both:
   * - `validations` (to show messages in the tooltips)
   * - `formIsValid` (to enable/disable the submit button)
   */
  useEffect(() => {
    const { validations: preValidations, isValid } = validateForm(formState, initialValidations)
    setValidations(preValidations)
    setFormIsValid(isValid)
  }, [formState])

  /**
   * Focuses the username field if there's a server error (e.g., wrong credentials) to prompt the user to correct it.
   */
  useEffect(() => {
    if (!!showServerError && userNameRef.current) userNameRef.current.focus()
  }, [showServerError])

  const userNameError = getFieldError<LoginFormState>(formState, validations, 'username')
  const passwordError = getFieldError<LoginFormState>(formState, validations, 'password')

  /** Opens the speed test inside the application's shared modal host. */
  function openSpeedTest () {
    openModal({ content: <SpeedTest /> })
  }

  return (
    process.env.NEXT_PUBLIC_FEATURE_LOGIN === "true" &&
      <form className={styles.form} onSubmit={handleSubmit} autoComplete={'on'} noValidate={false} data-testid="Login">
        <div className={styles.loginWrapper}>
          <div className={styles.contentWrapper}>
            <h1 className={`title2 ${styles.title}`} data-testid="Login_title">{t('title')}</h1>

            <div className={styles.fields}>
              <TooltipField
                validations={validations.username}
                placement="bottom-start"
                arrow={false}
                disableHoverListener={true}
                disableTouchListener={true}
                hasHelperText={true}
              >
                <TextField
                  type="text"
                  name="username"
                  autoComplete="username"
                  label={t('loginLabel')}
                  value={formState.username.value}
                  error={!!showServerError || (userNameError.hasError && !usernameFocused)}
                  helperText={!usernameFocused && userNameError.msg !== undefined ? t(`errors.fields.${userNameError.msg}`) : ' '}
                  onChange={handleChange}
                  inputRef={userNameRef}
                  autoFocus={true}
                  onFocus={() => setUsernameFocused(true)}
                  onBlur={() => setUsernameFocused(false)}
                  data-testid={'Login_username'}
                  slotProps={{
                    input: {
                      autoComplete: "username"
                    }
                  }}
                />
              </TooltipField>

              <TooltipField
                validations={validations.password}
                placement="bottom-start"
                arrow={false}
                disableHoverListener={true}
                disableTouchListener={true}
                hasHelperText={true}
              >
                <TextField
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  label={t('passwordLabel')}
                  value={formState.password.value}
                  error={passwordError.hasError || !!showServerError}
                  helperText={!!showServerError ? t(`errors.server.${showServerError}`) : passwordError.msg !== undefined ? t(`errors.fields.${passwordError.msg}`) : ' '}
                  onChange={handleChange}
                  data-testid={'Login_password'}
                  slotProps={{
                    input: {
                      autoComplete: "current-password",
                      endAdornment:
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(prev => !prev)}
                          edge="end"
                        >
                          {showPassword
                            ? <IconHidePassword className={styles.passwordIcon} />
                            : <IconShowPassword className={styles.passwordIcon} />
                          }
                        </IconButton>
                      </InputAdornment>
                    }
                  }}
                />
              </TooltipField>

              {FEATURES.recoverPassword &&
              <AppLink 
                data-testid="Login_forgotPassword"
                className={styles.forgotPasswordLink}
                href={recoverPasswordLink}
                target={recoverPasswordLink.startsWith('http') ? '_blank' : undefined}
              >
                {t('forgotPassword')}
              </AppLink>
              }
            </div>
          </div>

          <div className={styles.btnWrapper}>
            <Button data-testid="Login_loginButton" className={styles.nextBtn} type={"submit"} size={ButtonSize.LARGE} disabled={!formIsValid || !!showServerError || login.isPending}>{t('loginButton')}</Button>
          </div>
        </div>
        
        {FEATURES.signUp &&
        <div className={styles.registrationDisclaimer}>
          <p className={'bodyTextMediumStrong'}>{t('noAccount')}</p>
          {signUpLink.length > 0 &&
          <AppLink
            data-testid="Login_register"
            className={`${styles.registerLink}`}
            highlight={true}
            href={signUpLink}
            target={signUpLink.startsWith('http') ? '_blank' : undefined}
          >
            {t('register')}
          </AppLink>
          }
        </div>
        }
        
        {FEATURES.speedTest &&
          <Button data-testid="Login_speedTestButton" type="button" size={ButtonSize.MEDIUM} onClick={openSpeedTest} className={styles.speedTest}>
            <IconSpeedTest />
            {t.has('speedTestButton') ? t('speedTestButton') : 'Test de velocidad'}
          </Button>
        }
      </form>
  )
}